import {HubOutlined} from '@mui/icons-material';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {TabPanel} from 'src/components/common/TabPanel';
import {LocationTable} from 'src/components/locations/LocationTable';
import {handleGetLocations} from '../../../pages/Locations';
import {FormattedMessage, useIntl} from 'react-intl';
import {Box, Tab, Tabs, TabsProps, Typography} from '@mui/material';
import {chargersApi, locationsApi, tagsApi} from 'src/api';
import {useBusinessId, useAppDispatch, useAppSelector} from 'src/app/hooks';
import {
    ChargePointSearchRequestData,
    ListParamsType,
    LocationSearchRequestData,
} from 'src/types';
import {ChargerSortFieldEnum, LocationSortFieldEnum} from 'src/enums';
import {panelBadgeSx, panelHeaderSx, panelTitleSx, tabSelectedSx, tabsBandProps, tabsBandSx, tabsRootSx, tabSx} from './CompanyInformation.sx';
import {RfidTable} from 'src/components/common/RfidTable';
import {CompanyInformationProps} from './CompanyInformation.types';
import {ChargerTable} from 'src/components/chargers/ChargerTable';
import {handleGetChargers} from 'src/pages/Chargers';
import {BexioAccountsTable} from 'src/components/common/BexioAccountsTable';
import {CompanyEmployeesTable} from 'src/components/common/CompanyEmployeesTable/CompanyEmployeesTable';
import {CustomInvoiceItemsTable} from 'src/components/common/CustomInvoiceItemsTable';
import {CompanyCredentialsTable} from 'src/components/common/CompanyCredentialsTable/CompanyCredentialsTable';

export const CompanyInformation: FC<CompanyInformationProps> = ({userData}) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const canSearchChargers = useCan('chargers.search');
    const canSearchRfidTags = useCan('rfidTags.search');
    const DEFAULT_CHARGERS_PAGE = 100;

    const companyId = userData?.company?.id ?? 0;


    const persona = usePersona();
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const {authInfo} = useAppSelector((state) => state.common);

    const [locationTrigger, locationResponse] = locationsApi.useLazyGetLocationsQuery();
    const [chargersTrigger, chargersResponse] = chargersApi.useLazyGetChargersQuery();

    // Get RFID tags using search API (same as /app/rfids page)
    const isCurrentUserEmployee = persona.isEmployee;
    const currentEmployeeId = authInfo?.employee?.id;

    // Only fetch tags if the user has permission to read RFID
    const canReadRfid = useCan('rfidTags.search');

    // Only fetch chargers if the user has permission to read chargers
    const canReadChargers = useCan('chargers.search');

    // The permission the endpoints behind this tab check, not a list of who is allowed to hold it.
    // Written as "back office, but not the platform administrator", it hid the tab from the one
    // account that can read every Bexio account there is.
    const canManageBexioAccounts = useCan('bexioAccounts.search');
    // The permission the employee search itself checks. Named as "back office minus the platform
    // administrator", it hid the company's own employees from the account that may read every one.
    const canViewEmployees = useCan('employees.search');
    // Check if a user can manage custom invoice items (matches backend roles)
    // Was a list of three role names mirroring the backend guard by hand, and it silently dropped
    // the business's own managers, so they lost the control here while the API allowed them.
    const canManageInvoiceItems = useCan('customInvoiceItems.create');
    // Reading the list and changing it are separate rights (V1.184.0). The tab opens on the read,
    // because answering "the login exists, the invitation was never accepted" is support's job and
    // not a reason to hand them the power to mint one; the controls inside check the write. It used to
    // be `companies.update`, which also carried the consumption-billing switch - so the company whose
    // logins these are could not see them at all.
    const canReadCredentials = useCan('companies.readCredentials');

    // Calculate tab indices dynamically based on permissions
    let tabIndex = 0;
    tabIndex++; // locations tab
    const chargersTabIndex = canReadChargers ? tabIndex++ : -1;
    const rfidTabIndex = canReadRfid ? tabIndex++ : -1;
    const bexioTabIndex = canManageBexioAccounts ? tabIndex++ : -1;
    const employeesTabIndex = canViewEmployees ? tabIndex++ : -1;
    const invoiceItemsTabIndex = canManageInvoiceItems ? tabIndex++ : -1;
    // Last in the chain, and it must stay last: these indices are a hand-rolled counter, so a tab
    // inserted in the middle silently renumbers every panel after it.
    const credentialsTabIndex = canReadCredentials ? tabIndex++ : -1;

    const {data: rfidData, refetch: refetchRfidData} = tagsApi.useGetTagsQuery({
        params: {
            page: 0,
            size: 100,
        },
        data: {
            businessId: businessId,
            sortAsc: false,
            sortField: 'CREATED_AT',
            keyword: '',
            companyId: companyId ? companyId : undefined,
            employeeId: isCurrentUserEmployee ? currentEmployeeId : undefined,
        },
    }, {
        skip: !canReadRfid, // Skip API call if the user doesn't have permission
    });

    const handleRfidDataRefresh = async () => {
        // First, invalidate the cache
        dispatch(tagsApi.util.invalidateTags(['tags']));

        // Wait a bit and then refetch
        setTimeout(async () => {
            await refetchRfidData();
            setRfidRefreshKey(prev => prev + 1);
        }, 100);
    };


    const [activeTab, setActiveTab] = useState<number>(0);
    const [rfidRefreshKey, setRfidRefreshKey] = useState<number>(0);

    const [locationTableParams, setLocationTableParams] = useState<ListParamsType>({
        page: 0,
        size: DEFAULT_CHARGERS_PAGE,
    });
    const [locationFilterParams, setLocationFilterParams] = useState<LocationSearchRequestData>({
        businessId: 0,
        city: '',
        country: '',
        keyword: '',
        sortAsc: true,
        sortField: LocationSortFieldEnum.ID,
    });
    const [chargersTableParams, setChargersTableParams] = useState<ListParamsType>({
        page: 0,
        size: DEFAULT_CHARGERS_PAGE,
    });
    const [chargersFilterParams, setChargersFilterParams] = useState<ChargePointSearchRequestData>({
        businessId: 0,
        chargePointStatus: undefined,
        city: '',
        connectorStatus: undefined,
        country: '',
        locationId: undefined,
        street: '',
        withoutDriver: false,
        withoutLocation: false,
        model: '',
        vendor: '',
        sortField: ChargerSortFieldEnum.CREATED_AT,
        sortAsc: false,
    });

    const handleSetTab: TabsProps['onChange'] = (_event, newValue) => {
        setActiveTab(newValue);
    };

    const locationsCallHandler = handleGetLocations(
        locationTrigger,
        businessId
    );

    const handleLocationChange = (value: LocationSearchRequestData) => {
        setLocationFilterParams(value);
        setLocationTableParams({
            page: 0,
            size: locationTableParams.size,
        });
        Object.keys(authInfo) &&
        locationsCallHandler(
            {...value, companyId},
            {
                page: 0,
                size: locationTableParams.size,
            }
        );
    };

    const handleLocationChangePage = (page: number) => {
        Object.keys(authInfo) &&
        locationsCallHandler(
            {...locationFilterParams, companyId},
            {
                page,
                size: locationTableParams.size,
            }
        );
        setLocationTableParams({
            page,
            size: locationTableParams.size,
        });
    };

    const handleLocationChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => {
        Object.keys(authInfo) &&
        locationsCallHandler(
            {...locationFilterParams, companyId},
            {
                page: 0,
                size: +e.target.value || DEFAULT_CHARGERS_PAGE,
            }
        );
        setLocationTableParams({
            page: 0,
            size: +e.target.value || DEFAULT_CHARGERS_PAGE,
        });
    };

    const chargersCallHandler = handleGetChargers(
        chargersTrigger,
        businessId
    );

    const handleChargersChange = (value: ChargePointSearchRequestData) => {
        if (!canReadChargers) return;

        setChargersFilterParams(value);
        setChargersTableParams({
            page: 0,
            size: chargersTableParams.size,
        });
        Object.keys(authInfo) &&
        chargersCallHandler(
            {...value, companyId},
            {
                page: 0,
                size: chargersTableParams.size,
            }
        );
    };

    const handleChargersChangePage = (page: number) => {
        if (!canReadChargers) return;

        Object.keys(authInfo).length &&
        chargersCallHandler(
            {...chargersFilterParams, companyId},
            {
                page,
                size: chargersTableParams.size,
            }
        );
        setChargersTableParams({
            page,
            size: chargersTableParams.size,
        });
    };

    const handleChargersChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => {
        if (!canReadChargers) return;

        Object.keys(authInfo).length &&
        chargersCallHandler(
            {...chargersFilterParams, companyId},
            {
                page: 0,
                size: +e.target.value || DEFAULT_CHARGERS_PAGE,
            }
        );
        setChargersTableParams({
            page: 0,
            size: +e.target.value || DEFAULT_CHARGERS_PAGE,
        });
    };


    /**
     * The first page of this company's locations.
     *
     * It used to take its page number out of `location.state`, which the row that opened this page
     * filled by pulling the trailing digits off the list's query string. That worked only while the
     * list's URL was nothing but `?page=3`; now it carries the whole filter, the digits are not at
     * the end, and the state arrived holding the entire query - so `+state.page` was `NaN` and the
     * search came back 400. Nothing restores a list from here any more: the browser's Back carries
     * the query in its own history entry, and the breadcrumb appends `rememberedListQuery`.
     */
    useEffect(() => {
        Object.keys(authInfo) &&
        locationsCallHandler({...locationFilterParams, companyId}, locationTableParams);
    }, []);

    useEffect(() => {
        // Only fetch chargers if the user has permission
        if (!canReadChargers) {
            return;
        }

        Object.keys(authInfo).length &&
        chargersCallHandler({...chargersFilterParams, companyId}, chargersTableParams);
    }, []);


    return (
        <Box sx={tabsRootSx}>
            <Box sx={panelHeaderSx}>
                <Box sx={panelBadgeSx}>
                    <HubOutlined/>
                </Box>
                <Typography sx={panelTitleSx}>
                    <FormattedMessage id="relationships"/>
                </Typography>
            </Box>
            <Box>
                <Box sx={tabsBandSx}>
                    <Tabs
                        {...tabsBandProps}
                        value={activeTab}
                        onChange={handleSetTab}
                        aria-label="company relationship tables"
                    >
                        <Tab
                            sx={tabSx}
                            label={intl.formatMessage({id: 'locations'})}
                        />
                        {canReadChargers && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'chargers'})}
                            />
                        )}
                        {canReadRfid && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'rfidsTabHeader'})}
                            />
                        )}
                        {canManageBexioAccounts && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'bexioAccounts'})}
                            />
                        )}
                        {canViewEmployees && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'employees'})}
                            />
                        )}
                        {canManageInvoiceItems && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'invoiceItems'})}
                            />
                        )}
                        {canReadCredentials && (
                            <Tab
                                sx={tabSx}
                                label={intl.formatMessage({id: 'credentials'})}
                            />
                        )}
                    </Tabs>
                </Box>

                {/* LOCATIONS TAB */}
                <TabPanel
                    sx={tabSelectedSx}
                    value={activeTab}
                    index={0}
                >
                    {locationResponse.data && (
                        <LocationTable
                            flush
                            data={locationResponse.data}
                            filters={locationFilterParams}
                            size={locationTableParams.size}
                            pageNumber={locationTableParams.page}
                            handleUpdateTableData={handleLocationChange}
                            handleChangePage={handleLocationChangePage}
                            handleChangeRowsNumber={handleLocationChangeRowsNumber}
                            isCompanyUser={true}
                        />
                    )}
                </TabPanel>

                {/* CHARGERS TAB */}
                {canSearchChargers && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={chargersTabIndex}
                    >
                        {chargersResponse.data && (
                            <ChargerTable
                                flush
                                data={chargersResponse.data}
                                filters={chargersFilterParams}
                                pageSize={chargersTableParams.size}
                                pageNumber={chargersTableParams.page}
                                handleUpdateTableData={handleChargersChange}
                                handleChangePage={handleChargersChangePage}
                                handleChangeRowsNumber={handleChargersChangeRowsNumber}
                                isCompanyUser={true}
                            />
                        )}
                    </TabPanel>
                                )}

                {/* RFIDS TAB */}
                {canSearchRfidTags && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={rfidTabIndex}
                    >
                        <RfidTable
                            key={`rfid-${rfidRefreshKey}-${rfidData?.content?.length || 0}`}
                            data={userData ? {
                                ...userData,
                                ocppTags: rfidData?.content || []
                            } as any : undefined}
                            isCompanyUser={true}
                            companyId={companyId}
                            onRefetchRfidData={handleRfidDataRefresh}
                        />
                    </TabPanel>
                                )}

                {/* BEXIO ACCOUNTS TAB */}
                {canManageBexioAccounts && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={bexioTabIndex}
                    >
                        <BexioAccountsTable
                            data={userData}
                        />
                    </TabPanel>
                )}

                {/* EMPLOYEES TAB */}
                {canViewEmployees && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={employeesTabIndex}
                    >
                        <CompanyEmployeesTable
                            companyId={companyId}
                            businessId={businessId || 0}
                        />
                    </TabPanel>
                )}

                {/* INVOICE ITEMS TAB */}
                {canManageInvoiceItems && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={invoiceItemsTabIndex}
                    >
                        <CustomInvoiceItemsTable
                            userId={userData?.id ?? 0}
                            businessId={businessId ?? 0}
                        />
                    </TabPanel>
                )}

                {/* CREDENTIALS TAB */}
                {canReadCredentials && (
                    <TabPanel
                        sx={tabSelectedSx}
                        value={activeTab}
                        index={credentialsTabIndex}
                    >
                        <CompanyCredentialsTable
                            companyId={companyId}
                            businessId={businessId || 0}
                        />
                    </TabPanel>
                )}
            </Box>
        </Box>
    );
};
